import { useAuth0 } from "@appretio/auth";
import { SheetContainer, SheetContainerProvider } from "@appretio/ui-kit";
import { Outlet, RouteObject, useLocation, useMatches } from "react-router-dom";
import { AppHeader } from "./AppHeader";
import { layoutContainer, main } from "./AppLayout.css";

interface AppLayoutProps {
  /**
   * The navigation routes for the application.
   */
  navRoutes: RouteObject[];
  /**
   * The background images for the application.
   */
  backgrounds?: Record<string, string>;
  /**
   * The default background for the application.
   */
  defaultBackground?: string;
}

type RouteHandle = {
  /**
   * The background image for the route.
   */
  background?: string;
  /**
   * Additional route metadata.
   */
  [key: string]: unknown;
};

export function AppLayout({
  navRoutes,
  defaultBackground = "inherit"
}: AppLayoutProps) {
  const { user, logout } = useAuth0();

  function handleLogout() {
    logout({
      logoutParams: {
        returnTo: window.location.origin
      }
    });
  }

  const matches = useMatches();
  const parentRoute = matches[matches.length - 2];
  const currentRoute = matches[matches.length - 1];

  const parentMeta = parentRoute?.handle as RouteHandle | undefined;
  const currentMeta = currentRoute?.handle as RouteHandle | undefined;

  const background =
    currentMeta?.background ?? parentMeta?.background ?? defaultBackground;

  const { pathname } = useLocation();

  return (
    <div
      data-testid="app-layout"
      style={{ background }}
      className={layoutContainer}
    >
      <AppHeader
        navRoutes={navRoutes}
        pathname={pathname}
        userName={user?.name ?? ""}
        onSignOut={handleLogout}
      />

      <main className={main}>
        <SheetContainerProvider>
          <Outlet />
          <SheetContainer />
        </SheetContainerProvider>
      </main>
    </div>
  );
}
